import React from 'react';
import { Container, Group, Title, Button, SimpleGrid, Card, Text, Badge, Stack, Modal, TextInput, Select, NumberInput, Skeleton, Alert } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconTemperature, IconPlus, IconAlertTriangle } from '@tabler/icons-react';
import { useCustomerSensors, useSensorAlerts, useRegisterSensor } from '@/hooks/usePortalApi';

export default function SensorDashboard() {
  const [opened, { open, close }] = useDisclosure(false);
  const { data: sensors, isLoading: loadingSensors } = useCustomerSensors();
  const { data: alerts, isLoading: loadingAlerts } = useSensorAlerts();
  const registerSensor = useRegisterSensor();

  const [formData, setFormData] = React.useState({
    sensor_id: '',
    location_name: '',
    sensor_type: '',
    min_temp: 0,
    max_temp: 0,
    alert_email: '',
  });

  const handleSubmit = async () => {
    try {
      await registerSensor.mutateAsync(formData);
      notifications.show({
        title: 'Success',
        message: 'Sensor registered successfully',
        color: 'green',
      });
      close();
      setFormData({
        sensor_id: '',
        location_name: '',
        sensor_type: '',
        min_temp: 0,
        max_temp: 0,
        alert_email: '',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to register sensor',
        color: 'red',
      });
    }
  };

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <Group>
          <IconTemperature size={28} />
          <Title order={2}>Temperature Sensors</Title>
        </Group>
        <Button leftSection={<IconPlus size={16} />} onClick={open}>
          Register Sensor
        </Button>
      </Group>

      {loadingSensors ? (
        <SimpleGrid cols={3} mb="xl">
          <Skeleton height={150} />
          <Skeleton height={150} />
          <Skeleton height={150} />
        </SimpleGrid>
      ) : sensors && sensors.length > 0 ? (
        <SimpleGrid cols={3} mb="xl">
          {sensors.map((sensor: any) => {
            const isExcursion = sensor.status === 'excursion';
            return (
              <Card key={sensor.id} shadow="sm" padding="lg" withBorder>
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text fw={600}>{sensor.location_name}</Text>
                    <Badge color={isExcursion ? 'red' : 'green'} variant="dot">
                      {isExcursion ? 'Excursion' : 'Normal'}
                    </Badge>
                  </Group>
                  <Text size="xl" fw={700} c={isExcursion ? 'red' : 'blue'}>
                    {sensor.current_temp}°F
                  </Text>
                  <Text size="xs" c="dimmed">
                    Last reading: {new Date(sensor.last_reading_time).toLocaleString()}
                  </Text>
                  <Text size="xs" c="dimmed">
                    Range: {sensor.min_threshold}°F - {sensor.max_threshold}°F
                  </Text>
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>
      ) : (
        <Text ta="center" c="dimmed" py="xl" mb="xl">No sensors registered</Text>
      )}

      <Title order={3} mb="md">Active Alerts</Title>
      {loadingAlerts ? (
        <Stack gap="xs">
          <Skeleton height={80} />
          <Skeleton height={80} />
        </Stack>
      ) : alerts && alerts.length > 0 ? (
        <Stack gap="md">
          {alerts.map((alert: any) => (
            <Alert
              key={alert.id}
              icon={<IconAlertTriangle size={20} />}
              title={`Temperature Excursion - ${alert.location_name}`}
              color="red"
              variant="filled"
            >
              <Text size="sm">
                Current temperature: {alert.current_temp}°F (threshold: {alert.min_threshold}°F - {alert.max_threshold}°F)
              </Text>
              <Text size="xs" mt="xs">
                Started: {new Date(alert.started_at).toLocaleString()}
              </Text>
            </Alert>
          ))}
        </Stack>
      ) : (
        <Text ta="center" c="dimmed" py="xl">No active alerts</Text>
      )}

      <Modal opened={opened} onClose={close} title="Register Sensor" size="md">
        <Stack>
          <TextInput
            label="Sensor ID"
            placeholder="Enter sensor ID"
            value={formData.sensor_id}
            onChange={(e) => setFormData({ ...formData, sensor_id: e.target.value })}
            required
          />
          <TextInput
            label="Location Name"
            placeholder="e.g., Warehouse A"
            value={formData.location_name}
            onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
            required
          />
          <Select
            label="Sensor Type"
            placeholder="Select type"
            data={[
              { value: 'temperature', label: 'Temperature' },
              { value: 'humidity', label: 'Humidity' },
              { value: 'combo', label: 'Combo' },
            ]}
            value={formData.sensor_type}
            onChange={(value) => setFormData({ ...formData, sensor_type: value || '' })}
            required
          />
          <NumberInput
            label="Minimum Temperature"
            placeholder="0"
            suffix="°F"
            value={formData.min_temp}
            onChange={(value) => setFormData({ ...formData, min_temp: Number(value) || 0 })}
            required
          />
          <NumberInput
            label="Maximum Temperature"
            placeholder="0"
            suffix="°F"
            value={formData.max_temp}
            onChange={(value) => setFormData({ ...formData, max_temp: Number(value) || 0 })}
            required
          />
          <TextInput
            label="Alert Email"
            placeholder="alerts@example.com"
            type="email"
            value={formData.alert_email}
            onChange={(e) => setFormData({ ...formData, alert_email: e.target.value })}
            required
          />
          <Button onClick={handleSubmit} loading={registerSensor.isPending}>
            Register Sensor
          </Button>
        </Stack>
      </Modal>
    </Container>
  );
}
